import type { VehicleType } from "@slfinrtl/ui-bff-routes"
import { stockStatusMessages, vehicleTypeMessages } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import type { RadioOption } from "src/components/form/radio/radioGroup/RadioGroup"
import { useConfig } from "src/hooks"
import { isInUseDisabled } from "../../common/isInUseDisabled"
import { stockStatusOptionMessages } from "./useStockStatusOptions"

export const useInUseOption = (
	vmsDataSource: "PVMS" | "ONE_VMS" | undefined,
	selectedVehicleType: VehicleType | undefined,
	hasMinimumSubsidizedUsageTime: boolean,
	usageTime?: number,
): RadioOption => {
	const { allVehicleTypesAvailable, subsidizedUsage } = useConfig()
	const { formatMessage } = useIntl()
	const inUseMessages = useMemo(() => {
		if (usageTime === undefined) {
			return undefined
		}
		const result: string[] = [
			formatMessage(stockStatusOptionMessages.inUse, {
				dayCount: usageTime,
			}),
		]

		if (
			hasMinimumSubsidizedUsageTime &&
			subsidizedUsage.minimumSubsidizedUsageTime !== undefined &&
			usageTime < subsidizedUsage.minimumSubsidizedUsageTime
		) {
			result.push(
				formatMessage(stockStatusOptionMessages.minimumSubsidizedUsageTime, {
					dayCount: subsidizedUsage.minimumSubsidizedUsageTime,
				}),
			)
		}

		return result
	}, [formatMessage, subsidizedUsage.minimumSubsidizedUsageTime, usageTime, hasMinimumSubsidizedUsageTime])

	return useMemo(() => {
		return {
			disabledHint: vmsDataSource
				? formatMessage(commonMessages.disabledForVmsVehicle, {
						value: vmsDataSource === "ONE_VMS" ? "OneVMS" : "PVMS",
					})
				: !allVehicleTypesAvailable && !usageTime
					? formatMessage(commonMessages.actionNotAvailable)
					: !selectedVehicleType
						? undefined
						: isInUseDisabled(selectedVehicleType)
							? formatMessage(stockStatusOptionMessages.notPossible, {
									vehicleType: formatMessage(vehicleTypeMessages[selectedVehicleType]),
								})
							: undefined,
			label: formatMessage(stockStatusMessages.IN_USE),
			message: inUseMessages,
			value: "IN_USE",
		}
	}, [inUseMessages, formatMessage, vmsDataSource, selectedVehicleType, allVehicleTypesAvailable, usageTime])
}
